import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { RentalType, RentalStatus } from "@/shared/types/enums";
import type { RentalAdminSearchParams } from "../types";
import { rentalAdminService } from "../services/rental-admin.service";

const DEFAULT_PAGE_SIZE = 10;

export interface RentalListFilters {
  rentalId?: number;
  contractNumber?: string;
  clientName?: string;
  rentalType?: RentalType;
  status?: RentalStatus;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
}

export function useRentalList(
  pageSize = DEFAULT_PAGE_SIZE,
  initialFilters: RentalListFilters = {},
) {
  const [data, setData] = useState<RentalResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<RentalListFilters>(initialFilters);
  const requestIdRef = useRef(0);
  // Controller da última request em voo. Ao disparar uma nova, aborta a
  // anterior — cancela na rede, não só ignora a resposta (requestIdRef).
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const params: RentalAdminSearchParams = {
          rentalId: filters.rentalId,
          contractNumber: filters.contractNumber,
          clientName: filters.clientName,
          // A UI seleciona um único tipo/status; o backend só aceita listas.
          rentalTypes: filters.rentalType ? [filters.rentalType] : undefined,
          statuses: filters.status ? [filters.status] : undefined,
          startDate: filters.startDate,
          endDate: filters.endDate,
          // Backend usa intervalo — mesma data em ambos os campos
          // restringe a apenas o dia escolhido pelo usuário.
          createdAtStartDate: filters.createdAt,
          createdAtEndDate: filters.createdAt,
          page: targetPage,
          size: pageSize,
        };
        const response = await rentalAdminService.search(params, {
          signal: controller.signal,
        });
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(err, "Erro ao carregar aluguéis");
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [filters, pageSize],
  );

  useEffect(() => {
    // Adiado para o próximo tick: evita setState síncrono no efeito e
    // cancela buscas obsoletas quando os filtros mudam em sequência.
    const timer = setTimeout(() => load(0), 0);
    return () => clearTimeout(timer);
  }, [load]);

  // Cleanup no unmount — cancela request em voo.
  useEffect(() => () => controllerRef.current?.abort(), []);

  const addLocal = useCallback((item: RentalResponseDTO) => {
    setData((prev) => [item, ...prev]);
    setTotalElements((prev) => prev + 1);
  }, []);

  const updateLocal = useCallback((updated: RentalResponseDTO) => {
    setData((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  }, []);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const reload = useCallback(() => load(page), [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    goToPage: load,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  };
}
